import React, { useState, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Goal, Vision } from '../../../src/api/client';
import { useTeamStore } from '../../../src/stores/teamStore';
import { decrypt, decryptEntityTitle, getKeyForTeam } from '../../../src/utils/encryption';
import { Measure, countRecordedMilestones } from '../../../src/utils/milestones';
import { GOAL_STATUS_COLORS } from '../../../src/theme';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { formatShortDate } from '../../../src/utils/format';
import { POLL_INTERVAL_MS } from '../../../src/constants';
import { deriveGoalStatus } from '../../../src/utils/displayStatus';
import { useRefetchOnFocus } from '../../../src/hooks/useRefetchOnFocus';
import { getLocalDateStr } from '../../../src/utils/format';
import FloatingActionButton from '../../../src/components/FloatingActionButton';
import DataStateView from '../../../src/components/DataStateView';
import LastUpdatedBanner from '../../../src/components/LastUpdatedBanner';
import FilterTabs from '../../../src/components/FilterTabs';
import Icon from '../../../src/components/Icon';
import { useGoalsList } from '../../../src/hooks/queries/useGoalsList';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'planned', label: 'Planned' },
  { key: 'completed', label: 'Done' },
] as const;

type FilterKey = typeof STATUS_FILTERS[number]['key'];

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  visionFilterBar: { backgroundColor: theme.colors.background, paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: theme.colors.border },
  visionDropdownButton: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, backgroundColor: theme.colors.bgTertiary, borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10 },
  visionDropdownText: { fontSize: 14, fontWeight: '600' as const, color: theme.colors.textSecondary, flex: 1 },
  visionDropdownChevron: { fontSize: 10, color: theme.colors.textDisabled, marginLeft: 8 },
  visionDropdownList: { backgroundColor: theme.colors.background, borderBottomWidth: 1, borderBottomColor: theme.colors.border, paddingHorizontal: 16 },
  visionDropdownItem: { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.colors.bgTertiary },
  visionDropdownItemActive: { backgroundColor: theme.colors.primaryBg, marginHorizontal: -16, paddingHorizontal: 16 },
  visionDropdownItemText: { fontSize: 14, color: theme.colors.textSecondary },
  listContent: { padding: 16, paddingBottom: 80 },
  goalCard: { backgroundColor: theme.colors.background, borderRadius: 12, padding: 16, marginBottom: 10, ...theme.shadows.sm },
  goalContent: { flex: 1 },
  goalTopRow: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'flex-start' as const, gap: 10, marginBottom: 8 },
  goalTitle: { fontSize: 16, fontWeight: '600' as const, color: theme.colors.textPrimary, lineHeight: 22, flex: 1 },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 },
  statusText: { fontSize: 12, fontWeight: '700' as const, textTransform: 'uppercase' as const },
  progressSection: { marginBottom: 10 },
  progressTrack: { height: 6, backgroundColor: theme.colors.bgTertiary, borderRadius: 3, overflow: 'hidden' as const, marginBottom: 4 },
  progressFill: { height: '100%' as const, backgroundColor: theme.colors.primary, borderRadius: 3 },
  progressText: { fontSize: 12, color: theme.colors.textMuted, fontWeight: '500' as const },
  goalMeta: { flexDirection: 'row' as const, alignItems: 'center' as const, flexWrap: 'wrap' as const, gap: 10 },
  visionLabel: { fontSize: 13, color: theme.colors.textMuted, fontWeight: '500' as const, flexShrink: 1 },
  dateText: { fontSize: 12, color: theme.colors.textDisabled, fontWeight: '500' as const },
  milestoneLabel: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 4 },
  milestoneText: { fontSize: 12, color: theme.colors.primary, fontWeight: '600' as const },
  emptyContainer: { flexGrow: 1, justifyContent: 'center' as const, alignItems: 'center' as const },
  emptyState: { alignItems: 'center' as const, paddingHorizontal: 40 },
  emptyIcon: { fontSize: 48, marginBottom: 16, color: theme.colors.borderMedium },
  emptyTitle: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textSecondary, marginBottom: 8 },
  emptySubtitle: { fontSize: 15, color: theme.colors.textDisabled, textAlign: 'center' as const, lineHeight: 22 },
});

export default function GoalScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const { data, isLoading, error, refetch, isRefetching, dataUpdatedAt, isStale } = useGoalsList();

  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [selectedVisionFilter, setSelectedVisionFilter] = useState<string>('all');
  const [showVisionDropdown, setShowVisionDropdown] = useState(false);
  const [goalTitles, setGoalTitles] = useState<Record<string, string>>({});
  const [goalDates, setGoalDates] = useState<Record<string, { startDate?: string; endDate?: string }>>({});
  const [visionTitles, setVisionTitles] = useState<Record<string, string>>({});
  const [milestoneProgress, setMilestoneProgress] = useState<Record<string, { recorded: number; total: number }>>({});
  useRefetchOnFocus(refetch);

  // The date the derived statuses are evaluated against; ticks so a goal
  // becomes "active" on its start date while the screen sits open.
  const [today, setToday] = useState(getLocalDateStr);
  useEffect(() => {
    const interval = setInterval(() => setToday(getLocalDateStr()), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // planned/active is a function of the (decrypted) start date vs today,
  // derived here rather than written back from every device.
  const goals = useMemo(() => {
    if (!data?.goals) return [];
    return data.goals.map((g) => {
      const status = deriveGoalStatus(g.status, goalDates[g.id]?.startDate, today);
      return status === g.status ? g : { ...g, status };
    });
  }, [data?.goals, goalDates, today]);

  const visionList = data?.visions ?? [];

  // Decrypt goal titles and dates
  useEffect(() => {
    if (!data?.goals) return;
    let cancelled = false;
    (async () => {
      const titleEntries = await Promise.all(
        data.goals.map(async (g) => [g.id, await decryptEntityTitle(g)] as const)
      );
      if (cancelled) return;
      setGoalTitles(Object.fromEntries(titleEntries));

      // Dispatch on the goal's team, as decryptEntityTitle does for the title
      // above. A bare decrypt() falls through to the personal device key
      // whatever `team_id` says, which silently blanked every date and
      // milestone count on shared-team goals while the title rendered fine.
      const keyFor = async (g: Goal): Promise<unknown | null> => {
        try { return await getKeyForTeam(g.team_id ?? null); } catch { return null; }
      };

      const dateEntries = await Promise.all(
        data.goals.map(async (g): Promise<[string, { startDate?: string; endDate?: string }]> => {
          const dates: { startDate?: string; endDate?: string } = {};
          const key = await keyFor(g);
          if (!key) return [g.id, dates];
          if (g.start_date_encrypted && g.encryption_metadata?.start_date_iv) {
            try { dates.startDate = await decrypt(g.start_date_encrypted, { iv: g.encryption_metadata.start_date_iv }, key); } catch {}
          }
          if (g.end_date_encrypted && g.encryption_metadata?.end_date_iv) {
            try { dates.endDate = await decrypt(g.end_date_encrypted, { iv: g.encryption_metadata.end_date_iv }, key); } catch {}
          }
          return [g.id, dates];
        })
      );
      if (cancelled) return;
      setGoalDates(Object.fromEntries(dateEntries));

      const msEntries = await Promise.all(
        data.goals
          .filter((g) => g.metrics_encrypted && g.encryption_metadata?.metrics_iv && g.milestone_count)
          .map(async (g): Promise<[string, { recorded: number; total: number }] | null> => {
            try {
              const key = await keyFor(g);
              if (!key) return null;
              const metricsJson = await decrypt(g.metrics_encrypted!, { iv: g.encryption_metadata.metrics_iv! }, key);
              const measures: Measure[] = JSON.parse(metricsJson);
              return [g.id, countRecordedMilestones(measures)];
            } catch { return null; }
          })
      );
      if (!cancelled) setMilestoneProgress(Object.fromEntries(msEntries.filter(Boolean) as Array<[string, { recorded: number; total: number }]>));
    })();
    return () => { cancelled = true; };
  }, [data?.goals]);

  // Decrypt vision titles
  useEffect(() => {
    if (!data?.visions) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        data.visions.map(async (v) => [v.id, await decryptEntityTitle(v)] as const)
      );
      if (!cancelled) setVisionTitles(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [data?.visions]);

  // Compute activity counts per goal
  const activityCounts = useMemo(() => {
    if (!data?.activities) return {} as Record<string, { total: number; done: number }>;
    const counts: Record<string, { total: number; done: number }> = {};
    data.activities.forEach((t) => {
      if (t.goal_id) {
        if (!counts[t.goal_id]) counts[t.goal_id] = { total: 0, done: 0 };
        counts[t.goal_id].total++;
        if (t.status === 'done') counts[t.goal_id].done++;
      }
    });
    return counts;
  }, [data?.activities]);

  const filteredGoals = useMemo(() => {
    let filtered = goals;
    if (selectedVisionFilter !== 'all') {
      filtered = filtered.filter((g) => g.vision_id === selectedVisionFilter);
    }
    if (activeFilter === 'completed') {
      filtered = filtered.filter((g) => g.status === 'completed');
    } else if (activeFilter !== 'all') {
      filtered = filtered.filter((g) => g.status === activeFilter);
    }
    return filtered;
  }, [goals, activeFilter, selectedVisionFilter]);

  const filterCounts = useMemo(() => ({
    all: goals.length,
    active: goals.filter((g) => g.status === 'active').length,
    planned: goals.filter((g) => g.status === 'planned').length,
    completed: goals.filter((g) => g.status === 'completed').length,
  }), [goals]);

  const selectedVisionLabel = useMemo(() => {
    if (selectedVisionFilter === 'all') return 'All Visions';
    return visionTitles[selectedVisionFilter] || 'All Visions';
  }, [selectedVisionFilter, visionTitles]);

  const renderGoal = ({ item }: { item: Goal }) => {
    const statusStyle = GOAL_STATUS_COLORS[item.status] || GOAL_STATUS_COLORS.planned;
    const visionName = item.vision_id ? visionTitles[item.vision_id] : null;
    const counts = activityCounts[item.id];
    const progress = counts && counts.total > 0 ? Math.round((counts.done / counts.total) * 100) : 0;

    return (
      <TouchableOpacity
        style={styles.goalCard}
        onPress={() => router.push(`/(tabs)/goals/${item.id}` as any)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${goalTitles[item.id] || 'Untitled'}, status ${item.status}${counts ? `, ${progress}% complete` : ''}`}
        testID={`goals.goal-card-${item.id}`}
      >
        <View style={styles.goalContent} accessible={false}>
          <View style={styles.goalTopRow} accessible={false}>
            <Text style={styles.goalTitle} numberOfLines={2} accessible={false}>
              {goalTitles[item.id] || 'Untitled'}
            </Text>
            <View style={[styles.statusBadge, { backgroundColor: statusStyle.bg }]} accessible={false}>
              <Text style={[styles.statusText, { color: statusStyle.text }]} accessible={false}>
                {item.status}
              </Text>
            </View>
          </View>

          {counts && counts.total > 0 && (
            <View style={styles.progressSection} accessible={false}>
              <View style={styles.progressTrack} accessible={false}>
                <View style={[styles.progressFill, { width: `${progress}%` }]} accessible={false} />
              </View>
              <Text style={styles.progressText} accessible={false}>{progress}% · {counts.done}/{counts.total} activities</Text>
            </View>
          )}

          <View style={styles.goalMeta} accessible={false}>
            {milestoneProgress[item.id] && milestoneProgress[item.id].total > 0 && (
              <View style={styles.milestoneLabel} accessible={false}>
                <Icon name="flag-outline" size={13} color={colors.primary} />
                <Text style={styles.milestoneText} accessible={false}>
                  {milestoneProgress[item.id].recorded}/{milestoneProgress[item.id].total} milestones
                </Text>
              </View>
            )}
            {visionName && (
              <Text style={styles.visionLabel} numberOfLines={1} accessible={false}>
                {'\u2728'} {visionName}
              </Text>
            )}
            {goalDates[item.id]?.endDate && (
              <Text style={styles.dateText} accessible={false}>
                Due: {formatShortDate(goalDates[item.id].endDate!)}
              </Text>
            )}
            {!goalDates[item.id]?.endDate && goalDates[item.id]?.startDate && (
              <Text style={styles.dateText} accessible={false}>
                Started: {formatShortDate(goalDates[item.id].startDate!)}
              </Text>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <DataStateView
      isLoading={isLoading}
      error={error}
      hasData={!!data}
      onRetry={refetch}
      entityName="goals"
      testIDPrefix="goals"
    >
    <View style={styles.container}>
      <LastUpdatedBanner dataUpdatedAt={dataUpdatedAt} isStale={isStale} />
      {/* Vision Filter Dropdown */}
      <View style={styles.visionFilterBar}>
        <TouchableOpacity
          style={styles.visionDropdownButton}
          onPress={() => setShowVisionDropdown(!showVisionDropdown)}
          accessibilityRole="button"
          accessibilityLabel={`Filter by vision: ${selectedVisionLabel}`}
          accessibilityState={{ expanded: showVisionDropdown }}
          testID="goals.vision-filter-button"
        >
          <Text style={styles.visionDropdownText} numberOfLines={1} accessible={false}>
            {selectedVisionLabel}
          </Text>
          <Text style={styles.visionDropdownChevron} accessible={false}>{showVisionDropdown ? '\u25B2' : '\u25BC'}</Text>
        </TouchableOpacity>
      </View>

      {showVisionDropdown && (
        <View style={styles.visionDropdownList}>
          <TouchableOpacity
            style={[styles.visionDropdownItem, selectedVisionFilter === 'all' && styles.visionDropdownItemActive]}
            onPress={() => { setSelectedVisionFilter('all'); setShowVisionDropdown(false); }}
          >
            <Text style={styles.visionDropdownItemText}>All Visions</Text>
          </TouchableOpacity>
          {visionList.map((v) => (
            <TouchableOpacity
              key={v.id}
              style={[styles.visionDropdownItem, selectedVisionFilter === v.id && styles.visionDropdownItemActive]}
              onPress={() => { setSelectedVisionFilter(v.id); setShowVisionDropdown(false); }}
            >
              <Text style={styles.visionDropdownItemText} numberOfLines={1}>{visionTitles[v.id] || 'Untitled'}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      <FilterTabs
        tabs={STATUS_FILTERS as unknown as Array<{ key: string; label: string }>}
        activeKey={activeFilter}
        onSelect={(key) => setActiveFilter(key as FilterKey)}
        counts={filterCounts}
        testIDPrefix="goals"
      />

      <FlatList
        data={filteredGoals}
        keyExtractor={(item) => item.id}
        renderItem={renderGoal}
        contentContainerStyle={filteredGoals.length === 0 ? styles.emptyContainer : styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={() => refetch()}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Text style={styles.emptyIcon}>{'\u25CE'}</Text>
            <Text style={styles.emptyTitle}>
              {activeFilter === 'all' ? 'No goals yet' : `No ${activeFilter} goals`}
            </Text>
            <Text style={styles.emptySubtitle}>
              {activeFilter === 'all'
                ? 'Tap the + button to create your first goal'
                : 'Goals matching this filter will appear here'}
            </Text>
          </View>
        }
      />
      <FloatingActionButton onPress={() => router.push('/(tabs)/goals/create')} accessibilityLabel="Create new goal" />
    </View>
    </DataStateView>
  );
}
